/**
 * Iter 210 — Equipment Visual Instruction Center.
 *
 * Backend contract (Iter209): all endpoints prefixed with /api.
 *   /equipment/{eid}/documents            (GET, POST multipart)
 *   /equipment/documents/{did}/file       (GET inline / ?download=1)
 *   /equipment/{eid}/photos               (GET, POST multipart)
 *   /equipment/photos/{pid}/process       (POST)
 *   /equipment/photos/{pid}/approval      (POST decision APPROVED|REJECTED)
 *   /equipment/photos/{pid}/{original|derived}  (GET inline)
 *   /equipment/{eid}/instruction/sources  (GET pre-generation review)
 *   /equipment/{eid}/instructions         (GET list)
 *   /equipment/instructions               (POST)
 *   /equipment/instructions/{iid}         (GET, PATCH, DELETE)
 *   /equipment/instructions/{iid}/transition   (POST target=IN_REVIEW|CHECKED|APPROVED|ACTIVE)
 *   /equipment/instructions/{iid}/revision     (POST reason)
 *   /equipment/instructions/{iid}/pdf     (GET inline PDF)
 *
 * No parallel workflow, no browser-side beautification, no fabricated data.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Modal, Btn, Select, Input, Textarea, Badge } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { FileText, Image as ImageIcon, ClipboardList, Upload,
         CheckCircle2, XCircle, RefreshCw, Wand2, Trash2, Eye, Send } from "lucide-react";

const DOC_TYPES = [
  ["MANUFACTURER_MANUAL", "Üretici Kullanım Kılavuzu"],
  ["TECHNICAL_DOCUMENT", "Teknik Doküman"],
  ["CALIBRATION_CERTIFICATE", "Kalibrasyon Sertifikası"],
  ["MAINTENANCE_SERVICE_DOCUMENT", "Bakım / Servis Belgesi"],
  ["OTHER", "Diğer"],
];
const PHOTO_CATEGORIES = [
  ["GENERAL_VIEW", "Genel Görünüm"],
  ["NAMEPLATE", "Cihaz Etiketi"],
  ["CONTROL_PANEL", "Kontrol Paneli"],
  ["CONNECTIONS", "Bağlantılar"],
  ["CLEANING_AREA", "Temizlik Alanı"],
  ["WARNING_LABEL", "Uyarı Etiketi"],
  ["OTHER", "Diğer"],
];
const catLabel = (v) => PHOTO_CATEGORIES.find(([k]) => k === v)?.[1] || v;
const typeLabel = (v) => DOC_TYPES.find(([k]) => k === v)?.[1] || v;
const STATUS_TONE = {
  ORIGINAL: "warn", READY_FOR_REVIEW: "warn", APPROVED: "ok", REJECTED: "err",
  DRAFT: "warn", IN_REVIEW: "warn", CHECKED: "warn",
  ACTIVE: "ok", SUPERSEDED: "inactive",
};
const SECTION_ORDER = [
  ["purpose", "1. Amaç"],
  ["scope", "2. Kapsam"],
  ["responsibilities", "3. Sorumluluklar"],
  ["device_description", "4. Cihazın Tanımı"],
  ["device_info", "5. Cihaz Bilgileri"],
  ["pre_use_checks", "6. Kullanım Öncesi Kontroller"],
  ["startup", "7. Çalıştırma"],
  ["during_use", "8. Kullanım Sırasında Dikkat"],
  ["shutdown", "9. Durdurma / Kapatma"],
  ["post_use", "10. Kullanım Sonrası"],
  ["cleaning", "11. Temizlik"],
  ["faults", "12. Arıza / Uygunsuzluk"],
  ["safety", "13. İSG Uyarıları"],
  ["related_records", "14. İlgili Kayıtlar"],
  ["related_documents", "15. İlgili Dokümanlar"],
];

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const imgUrl = (path) => `${API}${path}`;

export default function EquipmentInstructionCenter({ equipment, open, onClose }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("documents");
  const [docs, setDocs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [sources, setSources] = useState(null);
  const [compare, setCompare] = useState(null); // photo obj to compare
  const [editingInst, setEditingInst] = useState(null); // instruction draft being edited

  const load = useCallback(async () => {
    if (!equipment?.id) return;
    try { setDocs(await api.get(`/equipment/${equipment.id}/documents`) || []); } catch { setDocs([]); }
    try { setPhotos(await api.get(`/equipment/${equipment.id}/photos`) || []); } catch { setPhotos([]); }
    try { setInstructions(await api.get(`/equipment/${equipment.id}/instructions`) || []); } catch { setInstructions([]); }
    try { setSources(await api.get(`/equipment/${equipment.id}/instruction/sources`)); } catch { setSources(null); }
  }, [equipment?.id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  // ─── documents ───
  const uploadDoc = async (file, document_type) => {
    if (!file || !document_type) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("document_type", document_type);
    try { await api.postForm(`/equipment/${equipment.id}/documents`, fd); load(); }
    catch (e) { alert(e.response?.data?.detail || "Yükleme başarısız"); }
  };
  const viewDoc = (did) => window.open(`${API}/equipment/documents/${did}/file`, "_blank");
  const dlDoc = (did) => window.open(`${API}/equipment/documents/${did}/file?download=1`, "_blank");
  const delDoc = async (did) => { if (confirm("Belge silinsin mi?")) { await api.delete(`/equipment/documents/${did}`); load(); } };

  // ─── photos ───
  const uploadPhoto = async (file, category) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category);
    try { await api.postForm(`/equipment/${equipment.id}/photos`, fd); load(); }
    catch (e) { alert(e.response?.data?.detail || "Yükleme başarısız"); }
  };
  const processPhoto = async (pid) => {
    try { await api.post(`/equipment/photos/${pid}/process`, {}); load(); }
    catch (e) { alert(e.response?.data?.detail || "İşleme başarısız"); }
  };
  const decidePhoto = async (pid, decision, reason) => {
    try { await api.post(`/equipment/photos/${pid}/approval`, { decision, reason: reason || null }); load(); setCompare(null); }
    catch (e) { alert(e.response?.data?.detail || "Karar başarısız"); }
  };
  const delPhoto = async (pid) => { if (confirm("Fotoğraf silinsin mi?")) { await api.delete(`/equipment/photos/${pid}`); load(); } };

  // ─── instructions ───
  const generateDraft = async () => {
    const initial = {};
    SECTION_ORDER.forEach(([k]) => { initial[k] = ""; });
    try {
      const inst = await api.post("/equipment/instructions", {
        equipment_id: equipment.id, sections: { purpose: "" },
        included_photo_ids: [],
      });
      setEditingInst({ ...inst, sections: { ...initial, ...(inst.sections || {}) } });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Taslak oluşturulamadı"); }
  };
  const saveInst = async () => {
    if (!editingInst?.id) return;
    const cleaned = Object.fromEntries(
      Object.entries(editingInst.sections || {}).filter(([, v]) => (v || "").trim())
    );
    try {
      const r = await api.patch(`/equipment/instructions/${editingInst.id}`, {
        sections: cleaned,
        included_photo_ids: editingInst.included_photo_ids || [],
        caption_overrides: editingInst.caption_overrides || {},
      });
      setEditingInst({ ...r, sections: { ...Object.fromEntries(SECTION_ORDER.map(([k]) => [k, ""])), ...(r.sections || {}) } });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const transition = async (target) => {
    if (!editingInst?.id) return;
    try { const r = await api.post(`/equipment/instructions/${editingInst.id}/transition`, { target }); setEditingInst({ ...editingInst, ...r }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Geçiş başarısız"); }
  };
  const newRevision = async () => {
    const reason = prompt("Yeni revizyon gerekçesi:");
    if (!reason) return;
    try { const r = await api.post(`/equipment/instructions/${editingInst.id}/revision`, { reason });
          const init = {}; SECTION_ORDER.forEach(([k]) => { init[k] = ""; });
          setEditingInst({ ...r, sections: { ...init, ...(r.sections || {}) } }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Revizyon açılamadı"); }
  };
  const previewInstPdf = async () => {
    if (!editingInst?.id) return;
    try { const blob = await api.getBlob(`/equipment/instructions/${editingInst.id}/pdf`);
          openPdfPreview(blob, `Kullanim_Talimati_${equipment.code || equipment.id.slice(0, 6)}.pdf`); }
    catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };
  const openInst = (inst) => {
    const init = {}; SECTION_ORDER.forEach(([k]) => { init[k] = ""; });
    setEditingInst({ ...inst, sections: { ...init, ...(inst.sections || {}) } });
  };

  const approvedPhotos = useMemo(() => photos.filter(p => p.status === "APPROVED"), [photos]);
  const canEdit = editingInst && !["APPROVED", "ACTIVE", "SUPERSEDED"].includes(editingInst.status);
  const activeInstruction = instructions.find(i => i.status === "ACTIVE");

  if (!equipment) return null;
  return (
    <Modal open={open} onClose={onClose} size="xl"
      title={`Kullanım Talimatı Merkezi — ${equipment.code || ""} · ${equipment.name || ""}`}>
      <div className="text-[11px] text-slate-500 border-b pb-2 mb-3" data-testid="eq-center-notice">
        Bu sayfa mevcut Meta Pharma kontrollü doküman sistemini kullanır. <b>Hiçbir teknik değer uydurulmaz</b>;
        eksik bilgiler için doğrulama istenir. Orijinal fotoğraflar asla değiştirilmez.
      </div>
      <div className="flex gap-1 border-b mb-3 text-xs">
        {[["documents", "Belgeler", FileText],
          ["photos", "Fotoğraflar", ImageIcon],
          ["instruction", "Kullanım Talimatı", ClipboardList]].map(([k, l, Ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`eq-tab-${k}`}
            className={`px-3 py-1.5 flex items-center gap-1 border-b-2 ${tab === k ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-600"}`}>
            <Ic size={12}/> {l}
          </button>
        ))}
      </div>

      {/* ─────────── BELGELER ─────────── */}
      {tab === "documents" && (
        <div data-testid="eq-documents-tab">
          <DocumentUploadBar onUpload={uploadDoc}/>
          <div className="text-[10px] bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-amber-900">
            Bu belgeler <b>DIŞ / DESTEKLEYİCİ KANITTIR</b>. Otomatik olarak Meta Pharma kontrollü dokümana (Fxx/Txx/Lxx) dönüşmez.
          </div>
          {docs.length === 0 ? <div className="text-xs italic text-slate-400 py-3">Belge yok.</div> :
            <table className="w-full text-xs border border-slate-200 rounded">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-2 py-1 text-left">Tür</th><th className="px-2 py-1 text-left">Dosya</th>
                <th className="px-2 py-1 text-left">Sürüm</th><th className="px-2 py-1 text-left">Tarih</th>
                <th className="px-2 py-1 text-left">Yükleyen</th><th className="px-2 py-1">Durum</th>
                <th className="px-2 py-1">İşlem</th>
              </tr></thead>
              <tbody>{docs.map(d => (
                <tr key={d.id} className="border-t" data-testid={`eq-doc-${d.id}`}>
                  <td className="px-2 py-1">{typeLabel(d.document_type)}</td>
                  <td className="px-2 py-1 font-mono">{d.file_name}</td>
                  <td className="px-2 py-1">{d.document_version || "—"}</td>
                  <td className="px-2 py-1">{(d.uploaded_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-1">{d.uploaded_by_name}</td>
                  <td className="px-2 py-1 text-center">{d.is_current
                    ? <Badge level="ok">Güncel</Badge> : <Badge level="inactive">Geçmiş</Badge>}</td>
                  <td className="px-2 py-1"><div className="flex gap-1">
                    <Btn size="sm" variant="ghost" onClick={() => viewDoc(d.id)} data-testid={`eq-doc-view-${d.id}`}><Eye size={10}/></Btn>
                    <Btn size="sm" variant="ghost" onClick={() => dlDoc(d.id)} data-testid={`eq-doc-dl-${d.id}`}>İndir</Btn>
                    <Btn size="sm" variant="danger" onClick={() => delDoc(d.id)} data-testid={`eq-doc-del-${d.id}`}><Trash2 size={10}/></Btn>
                  </div></td>
                </tr>))}</tbody>
            </table>}
        </div>
      )}

      {/* ─────────── FOTOĞRAFLAR ─────────── */}
      {tab === "photos" && (
        <div data-testid="eq-photos-tab">
          <PhotoUploadBar onUpload={uploadPhoto}/>
          {photos.length === 0 ? <div className="text-xs italic text-slate-400 py-3">Fotoğraf yok.</div> :
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{photos.map(p => (
              <div key={p.id} className="border rounded p-2 text-xs" data-testid={`eq-photo-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <Badge level={STATUS_TONE[p.status] || "warn"}>{p.status}</Badge>
                  <span className="text-[10px] text-slate-500">{catLabel(p.category)}</span>
                </div>
                <img src={imgUrl(`/equipment/photos/${p.id}/original`)} alt="original"
                  className="w-full h-28 object-cover rounded bg-slate-100"/>
                <div className="text-[10px] text-slate-500 mt-1">{(p.uploaded_at || "").slice(0, 10)}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {p.status === "ORIGINAL" &&
                    <Btn size="sm" variant="secondary" onClick={() => processPhoto(p.id)} data-testid={`eq-photo-process-${p.id}`}><Wand2 size={10}/> Profesyonel Hazırla</Btn>}
                  {p.status === "READY_FOR_REVIEW" &&
                    <Btn size="sm" onClick={() => setCompare(p)} data-testid={`eq-photo-compare-${p.id}`}>Karşılaştır</Btn>}
                  {p.status === "APPROVED" &&
                    <Btn size="sm" variant="secondary" onClick={() => setCompare(p)} data-testid={`eq-photo-view-${p.id}`}>Görüntüle</Btn>}
                  {p.status === "REJECTED" &&
                    <Btn size="sm" variant="secondary" onClick={() => processPhoto(p.id)} data-testid={`eq-photo-reprocess-${p.id}`}><RefreshCw size={10}/> Yeniden İşle</Btn>}
                  <Btn size="sm" variant="danger" onClick={() => delPhoto(p.id)} data-testid={`eq-photo-del-${p.id}`}><Trash2 size={10}/></Btn>
                </div>
              </div>))}</div>}
          {compare && <PhotoCompareModal photo={compare} equipment={equipment}
            onClose={() => setCompare(null)}
            onDecide={(dec, reason) => decidePhoto(compare.id, dec, reason)}
            onReprocess={() => processPhoto(compare.id)}/>}
        </div>
      )}

      {/* ─────────── KULLANIM TALİMATI ─────────── */}
      {tab === "instruction" && (
        <div data-testid="eq-instruction-tab">
          {!editingInst && (
            <>
              <SourceReview sources={sources} activeInstruction={activeInstruction}/>
              <div className="flex justify-end mt-2">
                <Btn onClick={generateDraft} data-testid="eq-instr-generate"><ClipboardList size={12}/> Kullanım Talimatı Hazırla</Btn>
              </div>
              {instructions.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Mevcut Talimatlar ({instructions.length})</div>
                  <table className="w-full text-xs border rounded">
                    <thead className="bg-slate-50"><tr>
                      <th className="px-2 py-1 text-left">Revizyon</th><th className="px-2 py-1">Durum</th>
                      <th className="px-2 py-1 text-left">Oluşturan</th><th className="px-2 py-1">Tarih</th>
                      <th className="px-2 py-1">İşlem</th>
                    </tr></thead>
                    <tbody>{instructions.map(i => (
                      <tr key={i.id} className="border-t" data-testid={`eq-instr-${i.id}`}>
                        <td className="px-2 py-1 font-mono">Rev {i.revision_no}</td>
                        <td className="px-2 py-1 text-center"><Badge level={STATUS_TONE[i.status] || "warn"}>{i.status}</Badge></td>
                        <td className="px-2 py-1">{i.created_by_name}</td>
                        <td className="px-2 py-1">{(i.created_at || "").slice(0, 10)}</td>
                        <td className="px-2 py-1 text-center">
                          <Btn size="sm" variant="secondary" onClick={() => openInst(i)} data-testid={`eq-instr-open-${i.id}`}>Aç</Btn>
                        </td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {editingInst && (
            <InstructionEditor
              inst={editingInst}
              setInst={setEditingInst}
              canEdit={canEdit}
              approvedPhotos={approvedPhotos}
              onSave={saveInst}
              onTransition={transition}
              onPreview={previewInstPdf}
              onNewRevision={newRevision}
              onClose={() => setEditingInst(null)}/>
          )}
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════ Sub-components ═══════════════════
function DocumentUploadBar({ onUpload }) {
  const [type, setType] = useState("MANUFACTURER_MANUAL");
  return (
    <div className="flex gap-2 items-end mb-3" data-testid="eq-doc-upload-bar">
      <Select label="Belge Türü" value={type} onChange={e => setType(e.target.value)} data-testid="eq-doc-type">
        {DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </Select>
      <label className="text-xs cursor-pointer bg-violet-600 text-white px-3 py-1.5 rounded flex items-center gap-1"
        data-testid="eq-doc-upload">
        <Upload size={12}/> Belge Yükle
        <input type="file" hidden accept="application/pdf,image/jpeg,image/png"
          onChange={e => { onUpload(e.target.files?.[0], type); e.target.value = ""; }}/>
      </label>
    </div>
  );
}

function PhotoUploadBar({ onUpload }) {
  const [cat, setCat] = useState("GENERAL_VIEW");
  return (
    <div className="flex gap-2 items-end mb-3" data-testid="eq-photo-upload-bar">
      <Select label="Kategori" value={cat} onChange={e => setCat(e.target.value)} data-testid="eq-photo-cat">
        {PHOTO_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </Select>
      <label className="text-xs cursor-pointer bg-violet-600 text-white px-3 py-1.5 rounded flex items-center gap-1"
        data-testid="eq-photo-upload">
        <Upload size={12}/> Fotoğraf Ekle
        <input type="file" hidden accept="image/jpeg,image/png"
          onChange={e => { onUpload(e.target.files?.[0], cat); e.target.value = ""; }}/>
      </label>
    </div>
  );
}

function PhotoCompareModal({ photo, equipment, onClose, onDecide, onReprocess }) {
  const [reason, setReason] = useState("");
  return (
    <Modal open={true} onClose={onClose} size="xl" title="ORİJİNAL ↔ DÜZENLENMİŞ">
      <div className="text-xs text-slate-500 mb-2">
        {equipment?.code} — {catLabel(photo.category)} · Yüklenme: {(photo.uploaded_at || "").slice(0, 10)}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] font-semibold text-slate-600 mb-1">ORİJİNAL</div>
          <img src={imgUrl(`/equipment/photos/${photo.id}/original`)} alt="original"
            className="w-full h-96 object-contain bg-slate-100 rounded" data-testid="eq-compare-original"/>
        </div>
        <div>
          <div className="text-[11px] font-semibold text-slate-600 mb-1">DÜZENLENMİŞ (Türetilmiş)</div>
          <img src={imgUrl(`/equipment/photos/${photo.id}/derived`)} alt="derived"
            className="w-full h-96 object-contain bg-slate-100 rounded" data-testid="eq-compare-derived"/>
        </div>
      </div>
      {photo.processing_meta?.steps_applied && (
        <div className="mt-2 text-[10px] text-slate-500">
          Uygulanan adımlar: {(photo.processing_meta.steps_applied || []).join(" · ")}
          {photo.processing_meta.perspective_applied && " · Perspektif düzeltmesi uygulandı"}
        </div>
      )}
      <div className="text-[10px] bg-amber-50 border border-amber-200 rounded p-2 mt-2 text-amber-900">
        Yalnızca onaylanmış düzenlenmiş görseller kullanım talimatı PDF'ine eklenebilir. Orijinal fotoğraflar delil / referanstır.
      </div>
      {photo.status === "READY_FOR_REVIEW" && (
        <>
          <Textarea label="Red gerekçesi (opsiyonel)" value={reason}
            onChange={e => setReason(e.target.value)} rows={2}/>
          <div className="flex justify-end gap-2 mt-3">
            <Btn variant="secondary" onClick={onReprocess} data-testid="eq-compare-reprocess"><RefreshCw size={12}/> Yeniden İşle</Btn>
            <Btn variant="danger" onClick={() => onDecide("REJECTED", reason)} data-testid="eq-compare-reject"><XCircle size={12}/> Reddet</Btn>
            <Btn onClick={() => onDecide("APPROVED")} data-testid="eq-compare-approve"><CheckCircle2 size={12}/> Onayla</Btn>
          </div>
        </>
      )}
      {photo.status !== "READY_FOR_REVIEW" && (
        <div className="flex justify-end gap-2 mt-3">
          <Btn variant="secondary" onClick={onClose}>Kapat</Btn>
        </div>
      )}
    </Modal>
  );
}

function SourceReview({ sources, activeInstruction }) {
  if (!sources) return <div className="text-xs italic text-slate-400 py-3">Kaynak özeti yükleniyor...</div>;
  const av = sources.available || {};
  return (
    <div className="border rounded p-3 bg-slate-50/50" data-testid="eq-source-review">
      <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Talimat Durumu</div>
      <div className="mb-3 text-xs">
        {activeInstruction
          ? <span className="flex items-center gap-2"><Badge level="ok">AKTİF</Badge>Rev {activeInstruction.revision_no} · {(activeInstruction.active_at || "").slice(0, 10)}</span>
          : <Badge level="warn">Aktif talimat yok</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="font-semibold text-emerald-700 mb-1">Kullanılabilir Kaynaklar</div>
          <ul className="space-y-0.5">
            {av.manuals && <li>✓ Üretici Kullanım Kılavuzu</li>}
            {av.technical && <li>✓ Teknik Doküman</li>}
            {av.calibration && <li>✓ Kalibrasyon Sertifikası</li>}
            {av.maintenance && <li>✓ Bakım / Servis Belgesi</li>}
            {(av.approved_photos || []).map(c => <li key={c}>✓ Onaylı {catLabel(c)}</li>)}
            {!av.manuals && !av.technical && (av.approved_photos || []).length === 0 &&
              <li className="italic text-slate-400">Onaylı kaynak yok.</li>}
          </ul>
        </div>
        <div>
          <div className="font-semibold text-amber-700 mb-1">Eksik / Uyarılar</div>
          <ul className="space-y-0.5">
            {(sources.warnings || []).map((w, i) => <li key={i}>⚠ {w}</li>)}
            {(sources.warnings || []).length === 0 && <li className="italic text-slate-400">Uyarı yok.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function InstructionEditor({ inst, setInst, canEdit, approvedPhotos, onSave, onTransition, onPreview, onNewRevision, onClose }) {
  const setSection = (k, v) => setInst({ ...inst, sections: { ...inst.sections, [k]: v } });
  const togglePhoto = (pid) => {
    const cur = inst.included_photo_ids || [];
    setInst({ ...inst, included_photo_ids: cur.includes(pid) ? cur.filter(x => x !== pid) : [...cur, pid] });
  };
  const setCaption = (pid, cap) => {
    setInst({ ...inst, caption_overrides: { ...(inst.caption_overrides || {}), [pid]: cap } });
  };
  const included = new Set(inst.included_photo_ids || []);
  const status = inst.status || "DRAFT";
  const actions = {
    DRAFT:      [{ label: "İncelemeye Gönder", target: "IN_REVIEW", icon: Send }],
    IN_REVIEW:  [{ label: "Kontrol Et", target: "CHECKED", icon: CheckCircle2 }],
    CHECKED:    [{ label: "Onayla", target: "APPROVED", icon: CheckCircle2 }],
    APPROVED:   [{ label: "Aktifleştir", target: "ACTIVE", icon: CheckCircle2 }],
    ACTIVE:     [],
  }[status] || [];

  return (
    <div data-testid="eq-instr-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs">
          <Badge level={STATUS_TONE[status] || "warn"}>{status}</Badge>
          <span>Rev {inst.revision_no}</span>
          {inst.created_by_name && <span className="text-slate-500">· Oluşturan: {inst.created_by_name}</span>}
        </div>
        <div className="flex gap-1">
          {canEdit && <Btn size="sm" variant="secondary" onClick={onSave} data-testid="eq-instr-save">Kaydet</Btn>}
          <Btn size="sm" onClick={onPreview} data-testid="eq-instr-preview"><Eye size={11}/> PDF Önizle</Btn>
          {actions.map(a => (
            <Btn key={a.target} size="sm" onClick={() => onTransition(a.target)} data-testid={`eq-instr-t-${a.target}`}>
              <a.icon size={11}/> {a.label}
            </Btn>
          ))}
          {status === "ACTIVE" &&
            <Btn size="sm" variant="secondary" onClick={onNewRevision} data-testid="eq-instr-new-rev"><RefreshCw size={11}/> Yeni Revizyon</Btn>}
          <Btn size="sm" variant="ghost" onClick={onClose}>Kapat</Btn>
        </div>
      </div>
      <div className="text-[10px] bg-sky-50 border border-sky-200 rounded p-2 mb-3 text-sky-900">
        <b>Teknik değerler uydurulamaz.</b> RPM/sıcaklık/süre/kimyasal gibi değerler boş bırakılabilir veya belge/foto/kullanıcı doğrulaması ile birlikte girilebilir.
      </div>
      {/* Section editor */}
      <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-1">
        {SECTION_ORDER.map(([k, l]) => (
          <div key={k} className="border rounded p-2 bg-white" data-testid={`eq-sec-${k}`}>
            <div className="text-[11px] font-semibold text-slate-700 mb-1">{l}</div>
            <Textarea rows={2} value={inst.sections?.[k] || ""}
              onChange={e => setSection(k, e.target.value)}
              disabled={!canEdit}
              placeholder="Bu bölüm için doğrulanmış içerik yoksa boş bırakın."
              data-testid={`eq-sec-input-${k}`}/>
          </div>
        ))}
      </div>
      {/* Approved photo picker */}
      <div className="mt-3 border-t pt-2">
        <div className="text-xs font-semibold uppercase text-slate-600 mb-1">Onaylı Fotoğraflar ({approvedPhotos.length})</div>
        {approvedPhotos.length === 0 && <div className="text-[11px] italic text-slate-400">Talimata eklenebilecek onaylı düzenlenmiş fotoğraf yok.</div>}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">{approvedPhotos.map(p => (
          <div key={p.id} className={`border rounded p-1 ${included.has(p.id) ? "border-emerald-400 bg-emerald-50" : ""}`}
            data-testid={`eq-instr-pick-${p.id}`}>
            <img src={imgUrl(`/equipment/photos/${p.id}/derived`)} alt="d" className="w-full h-20 object-cover rounded"/>
            <div className="text-[10px] text-slate-600 truncate">{catLabel(p.category)}</div>
            <label className="text-[10px] flex items-center gap-1 mt-0.5">
              <input type="checkbox" disabled={!canEdit} checked={included.has(p.id)} onChange={() => togglePhoto(p.id)}
                data-testid={`eq-instr-pick-cb-${p.id}`}/>
              Dahil et
            </label>
            {included.has(p.id) && (
              <Input placeholder="Şekil açıklaması" disabled={!canEdit}
                value={(inst.caption_overrides || {})[p.id] || ""}
                onChange={e => setCaption(p.id, e.target.value)}
                className="text-[10px] mt-1" data-testid={`eq-instr-pick-cap-${p.id}`}/>
            )}
          </div>))}</div>
      </div>
    </div>
  );
}
